import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, GitCompare, Loader2 } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { compareResearchSessions } from "@/lib/research-export.functions";
import { toast } from "sonner";
import { getLocaleTag } from "@/lib/i18n/format";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/_authenticated/app/research/compare")({
  component: ComparePage,
});

type Sess = { id: string; title: string; updated_at: string };

function ComparePage() {
  const [sessions, setSessions] = useState<Sess[]>([]);
  const [aId, setAId] = useState<string>("");
  const [bId, setBId] = useState<string>("");
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const compareFn = useServerFn(compareResearchSessions);

  useEffect(() => {
    supabase.from("research_sessions")
      .select("id, title, updated_at")
      .order("updated_at", { ascending: false })
      .limit(50)
      .then(({ data }) => setSessions((data ?? []) as Sess[]));
  }, []);

  const run = async () => {
    if (!aId || !bId || aId === bId) {
      toast.error("Seleziona due sessioni diverse");
      return;
    }
    setLoading(true);
    try {
      const r: any = await compareFn({ data: { sessionAId: aId, sessionBId: bId } });
      if (!r.ok) throw new Error(r.error);
      setResult(r);
    } catch (e: any) {
      toast.error(e?.message ?? "Errore");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center justify-between border-b border-border/60 px-4 py-3">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="icon">
            <Link to="/app/research"><ArrowLeft className="h-4 w-4" /></Link>
          </Button>
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Confronto sessioni</p>
            <h1 className="font-display text-lg font-semibold">Differenze normative tra ricerche</h1>
          </div>
        </div>
        <Button onClick={run} disabled={loading || !aId || !bId} className="gap-2">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <GitCompare className="h-4 w-4" />}
          Confronta
        </Button>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-8">
        <div className="grid gap-3 md:grid-cols-2">
          <SessionPicker label="Sessione A" value={aId} onChange={setAId} options={sessions} />
          <SessionPicker label="Sessione B" value={bId} onChange={setBId} options={sessions} />
        </div>

        {result && (
          <>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <SessionColumn label="A" data={result.a} />
              <SessionColumn label="B" data={result.b} />
            </div>
            <Card className="mt-6 border-border/60 p-5">
              <h2 className="font-display text-base font-semibold">Differenze normative</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Citazioni numeriche [CIT. n] estratte dalle risposte di ciascuna sessione.
              </p>
              <div className="mt-4 grid gap-3 md:grid-cols-3">
                <DiffBox title="Solo A" items={result.diff.onlyA} tone="rose" />
                <DiffBox title="Condivise" items={result.diff.shared} tone="emerald" />
                <DiffBox title="Solo B" items={result.diff.onlyB} tone="sky" />
              </div>
            </Card>
          </>
        )}

        {!result && !loading && (
          <p className="mt-10 text-center text-sm text-muted-foreground">
            Seleziona due sessioni e premi “Confronta” per vedere le risposte affiancate e il diff delle citazioni.
          </p>
        )}
      </div>
    </div>
  );
}

function SessionPicker({ label, value, onChange, options }: {
  label: string; value: string; onChange: (v: string) => void; options: Sess[];
}) {
  return (
    <Card className="border-border/60 p-4">
      <p className="mb-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger><SelectValue placeholder="Scegli una sessione..." /></SelectTrigger>
        <SelectContent>
          {options.map((s) => (
            <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Card>
  );
}

function SessionColumn({ label, data }: { label: string; data: any }) {
  const { i18n } = useTranslation();
  if (!data?.session) return null;
  const s = data.session;
  return (
    <Card className="border-border/60 p-4">
      <div className="flex items-center justify-between">
        <Badge variant="outline">Sessione {label}</Badge>
        <span className="text-[11px] text-muted-foreground">{new Date(s.updated_at).toLocaleDateString(getLocaleTag(i18n.resolvedLanguage))}</span>
      </div>
      <h3 className="mt-2 font-display text-base font-semibold">{s.title}</h3>
      <div className="mt-3 max-h-[420px] space-y-3 overflow-y-auto">
        {(data.messages ?? []).map((m: any, i: number) => (
          <div key={i} className={`rounded-md border border-border/60 p-3 text-sm ${m.role === "user" ? "bg-card" : "bg-muted/30"}`}>
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              {m.role === "user" ? "Quesito" : "LexAIPro"}
            </p>
            <p className="whitespace-pre-wrap leading-relaxed">{m.content}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}

function DiffBox({ title, items, tone }: { title: string; items: string[]; tone: "rose" | "emerald" | "sky" }) {
  const colors = {
    rose: "border-rose-500/30 bg-rose-500/5 text-rose-300",
    emerald: "border-emerald-500/30 bg-emerald-500/5 text-emerald-300",
    sky: "border-sky-500/30 bg-sky-500/5 text-sky-300",
  }[tone];
  return (
    <div className={`rounded-md border p-3 ${colors}`}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em]">{title} ({items.length})</p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {items.length === 0
          ? <span className="text-xs opacity-70">Nessuna</span>
          : items.map((n) => <span key={n} className="rounded border border-current/40 px-1.5 py-0.5 font-mono text-[10px]">CIT. {n}</span>)}
      </div>
    </div>
  );
}
